/**
 * 环境自检页（常驻路由 #/diagnostics）
 *
 * 保留理由：用户报"卡/黑屏/进不去导演台"时，光靠描述基本定位不了。
 * 给一个随时能打开、能一键复制探测报告的页面，排障成本从"来回追问"降到"贴一段文本"。
 */
import { useState } from 'react';

import { browserRecommendation } from '../../platform/browserGuide';
import type { QualityTier } from '../../platform/capability';
import { formatCapabilityReport } from '../../platform/directorGate';
import { listQualityTiers } from '../../platform/qualityPresets';
import { Modal, type ModalTone } from '../../ui/Modal';
import { useAppCapability } from '../capabilityContext';

const TIER_LABEL: Record<QualityTier, string> = { low: '流畅', mid: '中', high: '高' };

/** 四类弹窗的预览文案（与真实使用场景一致，便于一眼比对风格） */
const TONE_PREVIEW: Record<ModalTone, { title: string; summary: string }> = {
  neutral: { title: '操作提示', summary: '中性弹窗：用于普通说明与信息告知。' },
  info: { title: '3D 导演台使用须知', summary: '信息弹窗：功能说明、能力介绍、引导类提示。' },
  warning: { title: '当前环境无法运行 3D 导演台', summary: '警告弹窗：能力受限，但其余功能不受影响。' },
  danger: { title: '确认删除该画布？', summary: '危险弹窗：不可逆操作，必须二次确认。' },
};

function tierRankOf(tier: QualityTier): number {
  return ['low', 'mid', 'high'].indexOf(tier);
}

const TONE_LABEL: Record<ModalTone, string> = {
  neutral: '中性',
  info: '信息',
  warning: '警告',
  danger: '危险确认',
};

export function DiagnosticsPage() {
  const { report, engine, preset, bounced, requestTier } = useAppCapability();
  const [copied, setCopied] = useState(false);
  const [previewTone, setPreviewTone] = useState<ModalTone | null>(null);

  if (report === null || preset === null) {
    return <div className="p-6 text-ui text-ink-muted">正在探测运行环境…</div>;
  }

  /** 判空后捕获已收窄的常量：函数声明会提升，直接引用 report 会报错 */
  const snapshot = report;

  async function copyReport(): Promise<void> {
    await navigator.clipboard.writeText(formatCapabilityReport(snapshot, engine));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <h1 className="mb-1 text-base font-semibold">环境自检</h1>
      <p className="mb-5 text-uiSm text-ink-muted">
        用于确认为什么 3D 导演台受限或画质被降档。反馈问题时附上本页信息可显著加快定位。
      </p>

      <section className="panel mb-4 p-4">
        <h2 className="mb-3 text-ui font-semibold text-ink-secondary">运行环境</h2>
        <dl className="grid grid-cols-2 gap-y-1.5 text-ui md:grid-cols-3">
          {field('浏览器内核', engine)}
          {field('WebGL2', report.webgl2 ? '可用' : '不可用')}
          {field('GPU', report.gpuRenderer ?? '未上报', true)}
          {field('软件渲染', report.softwareRenderer ? '是' : '否')}
          {field('DPR', String(report.devicePixelRatio))}
          {field('内存', report.deviceMemoryGiB === null ? '未公开' : `${report.deviceMemoryGiB} GiB`)}
          {field('CPU 逻辑核', report.hardwareConcurrency === null ? '未公开' : String(report.hardwareConcurrency))}
          {field('判定档位', TIER_LABEL[report.tier])}
          {field('导演台', report.directorAvailable ? '可用' : '受限')}
        </dl>
        <div className="mt-3">
          <button type="button" className="btn-ghost" onClick={() => void copyReport()}>
            {copied ? '已复制' : '复制探测报告'}
          </button>
        </div>
      </section>

      <section className="panel mb-4 p-4">
        <h2 className="mb-1 text-ui font-semibold text-ink-secondary">画质档位</h2>
        <p className="mb-3 text-uiSm text-ink-muted">
          上限由探测结果决定；高于「{TIER_LABEL[report.tier]}」的选项标注不推荐，选中会自动弹回。
        </p>
        <div className="flex flex-wrap gap-2">
          {listQualityTiers().map((tier) => {
            const above = tierRankOf(tier) > tierRankOf(report.tier);
            const active = preset.tier === tier;
            return (
              <button
                key={tier}
                type="button"
                onClick={() => requestTier(tier)}
                className={active ? 'btn btn-primary' : 'btn-ghost'}
              >
                {TIER_LABEL[tier]}
                {above ? <span className="ml-1 text-uiSm text-state-warn">· 不推荐</span> : null}
              </button>
            );
          })}
        </div>

        {bounced !== null ? (
          <p className="mt-2 text-uiSm text-state-warn">
            已自动回到「{TIER_LABEL[report.tier]}」：当前机器无法稳定承载「{TIER_LABEL[bounced]}」档，
            强行开启会明显掉帧。
          </p>
        ) : null}

        <dl className="mt-3 grid grid-cols-2 gap-y-1 text-uiSm md:grid-cols-4">
          {field('渲染倍率', String(preset.renderScale))}
          {field('DPR 上限', String(preset.dprCap))}
          {field('阴影', preset.shadows ? '开' : '关')}
          {field('网格/标签', preset.showGrid ? '开' : '关')}
          {field('阵列上限', String(preset.crowdMaxInstances))}
          {field('素体细节', preset.bodyLod)}
          {field('截图宽度', `${preset.captureWidth}px`)}
          {field('全景分段', preset.panoramaSegments.join('×'))}
        </dl>
      </section>

      <section className="panel p-4">
        <h2 className="mb-2 text-ui font-semibold text-ink-secondary">推荐浏览器</h2>
        <ul className="list-disc space-y-1 pl-5 text-uiSm text-ink-muted">
          {browserRecommendation().map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      {/*
        浮层样式预览：高配机器上闸门弹窗永远不会触发，没有这个入口就无法在浏览器里
        验证弹窗风格与配色是否统一（我们已在“只看编译不真实看”上翻过两次车）。
      */}
      <section className="panel mt-4 p-4">
        <h2 className="mb-1 text-ui font-semibold text-ink-secondary">浮层样式预览</h2>
        <p className="mb-3 text-uiSm text-ink-muted">
          四类弹窗共用同一容器，只改语义条与图标颜色；用于校验与全站配色的一致性。
        </p>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(TONE_PREVIEW) as ModalTone[]).map((tone) => (
            <button
              key={tone}
              type="button"
              className="btn-ghost"
              onClick={() => setPreviewTone(tone)}
            >
              {TONE_LABEL[tone]}
            </button>
          ))}
        </div>
      </section>

      <Modal
        open={previewTone !== null}
        onClose={() => setPreviewTone(null)}
        tone={previewTone ?? 'neutral'}
        title={previewTone === null ? '' : TONE_PREVIEW[previewTone].title}
        summary={previewTone === null ? '' : TONE_PREVIEW[previewTone].summary}
        footer={
          <>
            {previewTone === 'danger' ? (
              /* 不可逆操作：主按钮用危险色，与左侧语义条一致，不用琥珀“诱骗”点击 */
              <button type="button" className="btn-danger" onClick={() => setPreviewTone(null)}>
                确认删除
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={() => setPreviewTone(null)}>
                知道了
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={() => setPreviewTone(null)}>
              取消
            </button>
          </>
        }
      >
        <p className="text-ui text-ink-secondary">
          正文区域：说明影响范围，并给出可执行的下一步，不能只写“不支持”。
        </p>
      </Modal>
    </div>
  );
}

function field(label: string, value: string, full = false) {
  return (
    <div className={['flex min-w-0 gap-2', full ? 'col-span-2 md:col-span-3' : ''].join(' ')}>
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      {/* GPU 串很长（含 ANGLE/驱动/后端），不能截断：截了就等于丢排障信息。
          改为整行换行展示 + title 悬浮全文 */}
      <dd className="min-w-0 flex-1 break-words text-ink-primary" title={value}>
        {value}
      </dd>
    </div>
  );
}
